import {registerMiddleware} from '@homeaway/redux-middleware-adapter';
import {triggerEdapPageView, triggerEdapEvent} from './edap';
import actions from '../actions/actions';

// actions
import accountCreationActions from '../actions/accountCreation';
import poActions from '../actions/purchaseOrder';
import softwareDetailsActions from '../actions/softwareDetails';
import businessModelActions from '../actions/businessModel';
import companyLocationsActions from '../actions/companyLocation';
import currencyActions from '../actions/currency';
import masterBrandActions from '../actions/masterBrand';
import helpActions from '../actions/help';
import mailingActions from '../actions/mailingInformation';
import vatActions from '../actions/vatInformation';
import confirmationPageActions from '../actions/confirmationPage';
import userInfoActions from '../actions/userInfo';
import gqlEligibilityActions from '../actions/gqlEligibility';
import reviewPageActions from '../actions/reviewPage';
import propertyCountActions from '../actions/propertyCount';
import webFormPageActions from '../actions/webFormPage';
import multipleAccountsActions from '../actions/multipleAccounts';
import brandDeterminationActions from '../actions/brandDetermination';
import confirmBrandActions from '../actions/confirmBrand';
import payoutDetailsActions from '../actions/payoutDetails';

// middleware handlers
import createAccount from './createAccount';
import getMailingMetadata from './getMailingMetadata';
import updateMailingDetails from './updateMailingDetails';
import getPurchaseOrder from './getPurchaseOrder';
import submitSoftwareDetails from './submitSoftwareDetails';
import validateSoftwareDetails from './validateSoftwareDetails';
import getSoftwareSuggestions from './getSoftwareSuggestions';
import getOnboardAdvertiserDetailsByEmail from './getOnboardAdvertiserDetailsByEmail';
import getLocationMetadata from './getLocationMetadata';
import submitCompanyLocation from './submitCompanyLocation';
import getCurrencies from './getCurrencies';
import updateCurrency from './updateCurrency';
import getUserInfo from './getUserInfo';
import recordApproval from './recordApproval';
import getMasterBrandData from './getMasterBrandData';
import addOwnerAccount from './addOwnerAccount';
import submitHelpForm from './submitHelpForm';
import getGQLEligiblity from './getGqlEligiblity';
import getVatMetadata from './getVatMetadata';
import updateVatDetails from './updateVatDetails';
import updateOnboardingStatus from './updateOnboardingStatus';
import updateOnboardingStatusToBMXIdentified from './updateOnboardingStatusToBMXIdentified';
import getReviewMetadata from './getReviewMetadata';
import updateAccountDetails from './updateAccountDetails';
import getListingMetadata from './getListingMetadata';
import updatePropertyCountInfo from './updatePropertyCountInfo';
import getSupportedCurrencies from './getSupportedCurrencies';
import createWebformAccount from './createWebformAccount';
import getWebformMetadata from './getWebformMetadata';
import confirmAccountMultipleAccounts from './confirmAccountMultipleAccounts';
import getSiteDetails from './getSiteDetails';
import getSupportedSites from './getSupportedSites';
import submitPayoutDetailsForm from './submitPayoutDetailsForm';
import getAdvertiserBusinessModel from './getAdvertiserBusinessModel';
import submitBusinessModel from './submitBusinessModel';
import getBrandSwitchRedirect from './getBrandSwitchRedirect';
import getPreferredBusinessModel from './getPreferredBusinessModel';

/**
 * The middleware directory holds functions that are meant to be called
 * when certain redux actions are dispatched. This is a pattern for consolidating
 * business logic and simplifying side effects handling, such as async api calls.
 *
 * It is a simpler alternative to redux-sagas, but accomplishes the same thing.
 * This is a cleaner pattern than redux-thunk or redux-promises.
 *
 * There is one true redux middleware, called the middlewareAdapter that allows us to
 * register middleware functions that hold our business logic. Each function is paired with
 * one action. When that action is dispatched, the function is called. The registrations
 * below are pairing up action-to-function.
 *
 * The functions, themselves, are passed the store and the action. They can dispatch
 * additional actions, as needed.
 *
 * Example:
 *  function doFetchData(store, action) {
 *      request('/api/getData').then((resp) => {
 *          store.dispatch(fetchSuccess(resp));
 *      });
 *  }
 *
 * registerMiddleware(FETCH_DATA, doFetchData);
 *
 * Once the above is setup, a component could dispatch the FETCH_DATA action creator,
 * our doFetchData function would therefore be called and when it succeeded in fetching
 * the data, dispatches the fetchSuccess action creator with the payload, presumably to
 * populate a reducer. More details here:
 * https://github.homeawaycorp.com/Catalyst/node-redux-middleware-adapter
 */

/* istanbul ignore next */
function registerAllMiddleware() {
    // List out all middleware functions and the action that they respond to
    registerMiddleware(actions.TRIGGER_EDAP_PAGEVIEW, triggerEdapPageView);
    registerMiddleware(actions.TRIGGER_EDAP_EVENT, triggerEdapEvent);
    registerMiddleware(actions.CREATE_OWNER_ACCOUNT, addOwnerAccount);
    registerMiddleware(accountCreationActions.CREATE_ACCOUNT, createAccount);
    registerMiddleware(webFormPageActions.CREATE_WEBFORM_ACCOUNT, createWebformAccount);
    registerMiddleware(mailingActions.GET_MAILING_METADATA, getMailingMetadata);
    registerMiddleware(mailingActions.UPDATE_MAILING_DETAILS, updateMailingDetails);
    registerMiddleware(poActions.GET_PURCHASE_ORDER, getPurchaseOrder);
    registerMiddleware(softwareDetailsActions.GET_SOFTWARE_SUGGESTIONS, getSoftwareSuggestions);
    registerMiddleware(softwareDetailsActions.GET_ONBOARD_ADVERTISER_DETAILS_BY_EMAIL, getOnboardAdvertiserDetailsByEmail);
    registerMiddleware(softwareDetailsActions.SUBMIT_SOFTWARE_DETAILS, submitSoftwareDetails);
    registerMiddleware(softwareDetailsActions.VALIDATE_SOFTWARE_DETAILS, validateSoftwareDetails);
    registerMiddleware(currencyActions.GET_CURRENCIES, getCurrencies);
    registerMiddleware(currencyActions.GET_SUPPORTED_CURRENCIES, getSupportedCurrencies);
    registerMiddleware(currencyActions.UPDATE_CURRENCY, updateCurrency);
    registerMiddleware(currencyActions.GET_SITE_DETAILS, getSiteDetails)
    registerMiddleware(userInfoActions.GET_USER_INFO, getUserInfo);
    registerMiddleware(poActions.RECORD_APPROVAL, recordApproval);
    registerMiddleware(companyLocationsActions.GET_LOCATION_METADATA, getLocationMetadata);
    registerMiddleware(companyLocationsActions.SUBMIT_COMPANY_LOCATION, submitCompanyLocation);
    registerMiddleware(companyLocationsActions.UPDATE_VAT_DETAILS, updateVatDetails);
    registerMiddleware(masterBrandActions.GET_MASTER_BRAND_DATA, getMasterBrandData);
    registerMiddleware(helpActions.SUBMIT_HELP_FORM, submitHelpForm);
    registerMiddleware(vatActions.GET_VAT_METADATA, getVatMetadata);
    registerMiddleware(vatActions.UPDATE_VAT_DETAILS, updateVatDetails);
    registerMiddleware(confirmationPageActions.VERIFY_PM_ELIGIBILITY, updateOnboardingStatus);
    registerMiddleware(confirmationPageActions.UPDATE_ONBOARDING_STATUS_TO_BMX_IDENTIFIED, updateOnboardingStatusToBMXIdentified);
    registerMiddleware(reviewPageActions.GET_REVIEW_METADATA, getReviewMetadata);
    registerMiddleware(reviewPageActions.UPDATE_ACCOUNT_DETAILS, updateAccountDetails);
    registerMiddleware(propertyCountActions.GET_LISTING_METADATA, getListingMetadata);
    registerMiddleware(propertyCountActions.UPDATE_PROPERTY_COUNT_INFO, updatePropertyCountInfo);
    registerMiddleware(webFormPageActions.GET_WEBFORM_METADATA, getWebformMetadata);
    registerMiddleware(gqlEligibilityActions.GET_GQL_ELIGIBLITY, getGQLEligiblity);
    registerMiddleware(multipleAccountsActions.CONFIRM_ACCOUNT, confirmAccountMultipleAccounts);
    registerMiddleware(brandDeterminationActions.GET_SUPPORTED_SITES, getSupportedSites);
    registerMiddleware(businessModelActions.GET_ADVERTISER_BUSINESS_MODEL, getAdvertiserBusinessModel);
    registerMiddleware(businessModelActions.SUBMIT_BUSINESS_MODEL, submitBusinessModel);
    registerMiddleware(businessModelActions.GET_PREFERRED_BUSINESS_MODEL, getPreferredBusinessModel);
    registerMiddleware(confirmBrandActions.REDIRECT_WITH_BRAND_SWITCH_PARAM, getBrandSwitchRedirect);
    registerMiddleware(payoutDetailsActions.GET_BANKING_COUNTRIES, getSupportedSites);
    registerMiddleware(payoutDetailsActions.SUBMIT_PAYOUT_DETAILS_FORM, submitPayoutDetailsForm);
}

export default registerAllMiddleware;
